'use client';

import { createClient } from '@/lib/supabase/client';

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '08') return true;
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

export interface UserApplication {
  id?: string;
  // Personal Information
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  // Contact Details
  address: string;
  city: string;
  province: string;
  postalCode: string;
  alternatePhone?: string;
  // Service Preferences
  servicePreferences: string[];
  otherServiceDescription?: string;
  // Moving Needs
  typicalItemTypes?: string;
  averageItemSize?: string;
  requiresAssemblyDisassembly: boolean;
  requiresPackingMaterials: boolean;
  // Frequency of Use
  frequencyOfUse: string;
  estimatedMonthlyBookings?: number;
  // Preferred Payment Method
  preferredPaymentMethod: string;
  // Special Requirements
  accessibilityRequirements?: string;
  specialHandlingNeeds?: string;
  preferredTimeSlots?: string;
  // Emergency Contact
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactRelationship: string;
  // Screening Questions
  howDidYouHear: string;
  whyChooseGogedit: string;
  previousMovingExperience?: string;
  additionalComments?: string;
  // Application Status
  status?: string;
  adminNotes?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  // Terms
  termsAccepted: boolean;
  privacyPolicyAccepted: boolean;
  marketingConsent: boolean;
  // Timestamps
  createdAt?: string;
  updatedAt?: string;
}

export const userApplicationService = {
  async submitApplication(application: UserApplication) {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('user_applications')
        .insert({
          first_name: application.firstName,
          last_name: application.lastName,
          email: application.email,
          phone: application.phone,
          date_of_birth: application.dateOfBirth,
          address: application.address,
          city: application.city,
          province: application.province,
          postal_code: application.postalCode,
          alternate_phone: application.alternatePhone,
          service_preferences: application.servicePreferences,
          other_service_description: application.otherServiceDescription,
          typical_item_types: application.typicalItemTypes,
          average_item_size: application.averageItemSize,
          requires_assembly_disassembly: application.requiresAssemblyDisassembly,
          requires_packing_materials: application.requiresPackingMaterials,
          frequency_of_use: application.frequencyOfUse,
          estimated_monthly_bookings: application.estimatedMonthlyBookings,
          preferred_payment_method: application.preferredPaymentMethod,
          accessibility_requirements: application.accessibilityRequirements,
          special_handling_needs: application.specialHandlingNeeds,
          preferred_time_slots: application.preferredTimeSlots,
          emergency_contact_name: application.emergencyContactName,
          emergency_contact_phone: application.emergencyContactPhone,
          emergency_contact_relationship: application.emergencyContactRelationship,
          how_did_you_hear: application.howDidYouHear,
          why_choose_gogedit: application.whyChooseGogedit,
          previous_moving_experience: application.previousMovingExperience,
          additional_comments: application.additionalComments,
          terms_accepted: application.termsAccepted,
          privacy_policy_accepted: application.privacyPolicyAccepted,
          marketing_consent: application.marketingConsent,
          status: 'pending',
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Submit application error:', error.message);
          return null;
        }
      }

      return {
        id: data.id,
        firstName: data.first_name,
        lastName: data.last_name,
        email: data.email,
        status: data.status,
        createdAt: data.created_at,
      };
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async getAllApplications() {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data, error } = await supabase
        .from('user_applications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Get applications error:', error.message);
          return [];
        }
      }

      return data?.map((row) => ({
        id: row.id,
        firstName: row.first_name,
        lastName: row.last_name,
        email: row.email,
        phone: row.phone,
        dateOfBirth: row.date_of_birth,
        address: row.address,
        city: row.city,
        province: row.province,
        postalCode: row.postal_code,
        alternatePhone: row.alternate_phone,
        servicePreferences: row.service_preferences,
        otherServiceDescription: row.other_service_description,
        typicalItemTypes: row.typical_item_types,
        averageItemSize: row.average_item_size,
        requiresAssemblyDisassembly: row.requires_assembly_disassembly,
        requiresPackingMaterials: row.requires_packing_materials,
        frequencyOfUse: row.frequency_of_use,
        estimatedMonthlyBookings: row.estimated_monthly_bookings,
        preferredPaymentMethod: row.preferred_payment_method,
        accessibilityRequirements: row.accessibility_requirements,
        specialHandlingNeeds: row.special_handling_needs,
        preferredTimeSlots: row.preferred_time_slots,
        emergencyContactName: row.emergency_contact_name,
        emergencyContactPhone: row.emergency_contact_phone,
        emergencyContactRelationship: row.emergency_contact_relationship,
        howDidYouHear: row.how_did_you_hear,
        whyChooseGogedit: row.why_choose_gogedit,
        previousMovingExperience: row.previous_moving_experience,
        additionalComments: row.additional_comments,
        status: row.status,
        adminNotes: row.admin_notes,
        reviewedBy: row.reviewed_by,
        reviewedAt: row.reviewed_at,
        termsAccepted: row.terms_accepted,
        privacyPolicyAccepted: row.privacy_policy_accepted,
        marketingConsent: row.marketing_consent,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      })) || [];
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async updateApplication(id: string, updates: Partial<UserApplication>) {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const updateData: any = {};
      if (updates.status) updateData.status = updates.status;
      if (updates.adminNotes !== undefined) updateData.admin_notes = updates.adminNotes;
      if (updates.reviewedBy) updateData.reviewed_by = updates.reviewedBy;
      if (updates.reviewedBy) updateData.reviewed_at = new Date().toISOString();

      const { data, error } = await supabase
        .from('user_applications')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Update application error:', error.message);
          return null;
        }
      }

      return data;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async deleteApplication(id: string) {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { error } = await supabase
        .from('user_applications')
        .delete()
        .eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Delete application error:', error.message);
          return false;
        }
      }

      return true;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async bulkUpdateStatus(ids: string[], status: string) {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { error } = await supabase
        .from('user_applications')
        .update({ 
          status,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString()
        })
        .in('id', ids);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Bulk update error:', error.message);
          return null;
        }
      }

      return true;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async bulkDelete(ids: string[]) {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { error } = await supabase
        .from('user_applications')
        .delete()
        .in('id', ids);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Bulk delete error:', error.message);
          return null;
        }
      }

      return true;
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },
};